import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Sparkles,
  CheckCircle,
  XCircle,
  RefreshCw,
  Wifi,
  WifiOff,
  Search,
  FileText,
  Target,
  Image,
  Radio,
  Clock,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useWebSocket, GenerationProgress, GenerationComplete, GenerationFailed, AgentUpdate } from '@/hooks/useWebSocket'
import { articlesApi } from '@/services/api'
import { useMutation, useQuery } from '@tanstack/react-query'

// V3 Pipeline stage configuration (4 stages: Planner → Writer → Reviewer → Image)
const STAGE_CONFIG: Record<string, { labelKey: string; icon: typeof Search; color: string }> = {
  planning: { labelKey: 'generationProgress.stages.planning.label', icon: Search, color: 'text-blue-500' },
  writing: { labelKey: 'generationProgress.stages.writing.label', icon: FileText, color: 'text-green-500' },
  reviewing: { labelKey: 'generationProgress.stages.reviewing.label', icon: Target, color: 'text-purple-500' },
  generating_image: { labelKey: 'generationProgress.stages.generating_image.label', icon: Image, color: 'text-cyan-500' },
}

// Ordered list of V3 stages
const STAGE_ORDER = ['planning', 'writing', 'reviewing', 'generating_image'] as const

// Map backend status to stage name (V3 pipeline)
const STATUS_TO_STAGE: Record<string, string> = {
  researching: 'planning',
  planning: 'planning',
  writing: 'writing',
  editing: 'reviewing',
  reviewing: 'reviewing',
  generating_image: 'generating_image',
}

// Map individual agent names to V3 stage names
const AGENT_TO_STAGE: Record<string, string> = {
  strategist: 'planning',
  planner: 'planning',
  planning: 'planning',
  writer: 'writing',
  writing: 'writing',
  finalizer: 'reviewing',
  reviewer: 'reviewing',
  reviewing: 'reviewing',
  image_generator: 'generating_image',
  generating_image: 'generating_image',
}

interface ArticleProgressProps {
  articleId: string
  topic: string
  onComplete?: () => void
  onClose?: () => void
}

// Helper to translate progress messages from the backend
// Returns translated message if available, otherwise returns original
function useTranslateProgressMessage() {
  const { t, i18n } = useTranslation()

  return useCallback((message: string | undefined): string => {
    if (!message) return t('articleProgress.starting')

    // Try exact match first
    const translationKey = `generationProgress.messages.${message}`
    const translated = t(translationKey, { defaultValue: '' })
    if (translated && translated !== translationKey) {
      return translated
    }

    // Handle dynamic messages like "Writing: Section Title" or "Draft complete: 1500 words"
    // Extract the base message before the colon
    const colonIndex = message.indexOf(':')
    if (colonIndex > 0) {
      const baseMessage = message.substring(0, colonIndex).trim()
      const detail = message.substring(colonIndex + 1).trim()
      const baseKey = `generationProgress.messages.${baseMessage}`
      const baseTranslated = t(baseKey, { defaultValue: '' })
      if (baseTranslated && baseTranslated !== baseKey) {
        return `${baseTranslated}: ${detail}`
      }
    }

    // Return original message if no translation found
    return message
  }, [t, i18n.language])
}

export function ArticleProgress({ articleId, topic, onComplete, onClose }: ArticleProgressProps) {
  const { t } = useTranslation()
  const translateMessage = useTranslateProgressMessage()
  const [progress, setProgress] = useState<GenerationProgress | null>(null)
  const [result, setResult] = useState<GenerationComplete | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [agentHistory, setAgentHistory] = useState<
    Array<{ agent: string; message: string; completed: boolean }>
  >([])
  const [usePolling, setUsePolling] = useState(false)

  const handleProgress = useCallback((data: GenerationProgress) => {
    // Normalize agent name to stage name for V3 pipeline
    const stageName = AGENT_TO_STAGE[data.agent] || data.agent
    const normalizedData = { ...data, agent: stageName }
    setProgress(normalizedData)

    // Track stage completion (using normalized stage names)
    setAgentHistory((prev) => {
      const existing = prev.find((h) => h.agent === stageName)
      if (existing) {
        return prev.map((h) =>
          h.agent === stageName
            ? { ...h, message: data.message, completed: data.status === 'completed' }
            : h
        )
      }
      return [...prev, { agent: stageName, message: data.message, completed: false }]
    })
  }, [])

  const handleComplete = useCallback(
    (data: GenerationComplete) => {
      setResult(data)
      setProgress(null)
      onComplete?.()
    },
    [onComplete]
  )

  const handleFailed = useCallback((data: GenerationFailed) => {
    setError(data.error)
    setProgress(null)
  }, [])

  const handleAgentUpdate = useCallback((data: AgentUpdate) => {
    // Normalize agent name to stage name for V3 pipeline
    const stageName = AGENT_TO_STAGE[data.agent] || data.agent

    // Update stage history with more detailed status
    setAgentHistory((prev) => {
      const existing = prev.find((h) => h.agent === stageName)
      if (existing) {
        return prev.map((h) =>
          h.agent === stageName
            ? { ...h, message: data.message, completed: data.agent_status === 'completed' }
            : h
        )
      }
      return [...prev, { agent: stageName, message: data.message, completed: false }]
    })
  }, [])

  const { isConnected } = useWebSocket({
    articleId,
    onProgress: handleProgress,
    onAgentUpdate: handleAgentUpdate,
    onComplete: handleComplete,
    onFailed: handleFailed,
    autoConnect: !usePolling,
  })

  // Fetch initial progress state on mount (restores state when navigating back)
  useEffect(() => {
    const fetchInitialProgress = async () => {
      try {
        const data = await articlesApi.getProgress(articleId)

        // If already completed
        if (data.status === 'ready' || data.status === 'published') {
          const article = await articlesApi.get(articleId)
          handleComplete({
            title: article.title,
            word_count: article.word_count || 0,
            seo_score: article.seo_score || 0,
            geo_score: article.geo_score || 0,
            human_score: article.human_score || 0,
            cost: article.generation_cost || 0,
            generation_time: null,
          })
          return
        }

        // If failed
        if (data.status === 'failed') {
          const failedAgent = data.agents?.find(a => a.status === 'failed')
          handleFailed({ error: failedAgent?.message || 'Generation failed' })
          return
        }

        // Restore current progress
        setProgress({
          agent: data.current_agent || STATUS_TO_STAGE[data.status] || 'planning',
          percent: data.progress,
          status: data.status,
          message: data.agents?.find(a => a.agent === data.current_agent)?.message || 'Processing...',
        })

        // Restore agent history from API data
        if (data.agents && data.agents.length > 0) {
          const history = data.agents.map(a => ({
            agent: a.agent,
            message: a.message || '',
            completed: a.status === 'completed',
          }))
          setAgentHistory(history)
        }
      } catch (err) {
        console.error('Failed to fetch initial progress:', err)
      }
    }

    fetchInitialProgress()
  }, [articleId, handleComplete, handleFailed])

  // Fallback to polling if WebSocket doesn't connect after 3 seconds
  useEffect(() => {
    if (!isConnected && !usePolling) {
      const timer = setTimeout(() => {
        setUsePolling(true)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isConnected, usePolling])

  // Polling fallback - fetch progress every 2 seconds
  const { data: pollData } = useQuery({
    queryKey: ['article-progress', articleId],
    queryFn: () => articlesApi.getProgress(articleId),
    enabled: usePolling && !result && !error,
    refetchInterval: 2000,
  })

  // Process polling data
  useEffect(() => {
    if (pollData && usePolling) {
      const agentName = pollData.current_agent ? STATUS_TO_STAGE[pollData.status] || pollData.current_agent : null

      if (pollData.status === 'ready') {
        // Fetch full article to get completion data
        articlesApi.get(articleId).then((article) => {
          handleComplete({
            title: article.title,
            word_count: article.word_count || 0,
            seo_score: article.seo_score || 0,
            geo_score: article.geo_score || 0,
            human_score: article.human_score || 0,
            cost: article.generation_cost || 0,
            generation_time: null,
          })
        })
      } else if (pollData.status === 'failed') {
        // Find the failed agent message
        const failedAgent = pollData.agents?.find(a => a.status === 'failed')
        handleFailed({ error: failedAgent?.message || 'Generation failed' })
      } else if (agentName) {
        const currentAgentProgress = pollData.agents?.find(a => a.agent === agentName)
        handleProgress({
          agent: agentName,
          percent: pollData.progress,
          status: pollData.status,
          message: currentAgentProgress?.message || `Processing with ${agentName}...`,
        })
      }
    }
  }, [pollData, usePolling, articleId, handleComplete, handleFailed, handleProgress])

  const retryMutation = useMutation({
    mutationFn: () => articlesApi.retry(articleId),
    onSuccess: () => {
      setError(null)
      setResult(null)
      setProgress(null)
      setAgentHistory([])
    },
  })

  const isGenerating = !result && !error
  const currentStage = progress?.agent ? STAGE_CONFIG[progress.agent] : null

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              {result ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  {t('articleProgress.generationComplete')}
                </>
              ) : error ? (
                <>
                  <XCircle className="h-5 w-5 text-destructive" />
                  {t('articleProgress.generationFailed')}
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 text-primary animate-pulse" />
                  {t('articleProgress.generatingArticle')}
                </>
              )}
            </CardTitle>
            <CardDescription className="mt-1">{topic}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <Wifi className="h-3 w-3" />
                {t('articleProgress.connectionStatus.live')}
              </span>
            ) : usePolling ? (
              <span className="flex items-center gap-1 text-xs text-blue-600">
                <Radio className="h-3 w-3 animate-pulse" />
                {t('articleProgress.connectionStatus.polling')}
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <WifiOff className="h-3 w-3" />
                {t('articleProgress.connectionStatus.connecting')}
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Bar */}
        {isGenerating && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                {currentStage && (
                  <>
                    <currentStage.icon className={`h-4 w-4 ${currentStage.color}`} />
                    {t(currentStage.labelKey)}
                  </>
                )}
              </span>
              <span className="font-medium">{progress?.percent || 0}%</span>
            </div>
            <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
              <div
                className="bg-primary h-full transition-all duration-500 ease-out"
                style={{ width: `${progress?.percent || 0}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground">{translateMessage(progress?.message)}</p>
          </div>
        )}

        {/* V3 Pipeline Stages */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">{t('generationProgress.title')}</h4>
          <div className="space-y-1">
            {STAGE_ORDER.map((key) => {
              const config = STAGE_CONFIG[key]
              if (!config) return null
              const historyItem = agentHistory.find((h) => h.agent === key)
              const isActive = progress?.agent === key
              const isCompleted = historyItem?.completed
              const StageIcon = config.icon

              return (
                <div
                  key={key}
                  className={`flex items-center gap-3 p-2 rounded-md transition-colors ${
                    isActive
                      ? 'bg-primary/10'
                      : isCompleted
                        ? 'bg-green-500/10'
                        : 'bg-secondary/50'
                  }`}
                >
                  <StageIcon
                    className={`h-4 w-4 ${
                      isActive
                        ? `${config.color} animate-pulse`
                        : isCompleted
                          ? 'text-green-500'
                          : 'text-muted-foreground'
                    }`}
                  />
                  <span
                    className={`flex-1 text-sm ${
                      isActive ? 'font-medium' : isCompleted ? 'text-green-700' : 'text-muted-foreground'
                    }`}
                  >
                    {t(config.labelKey)}
                  </span>
                  {isCompleted && <CheckCircle className="h-4 w-4 text-green-500" />}
                  {isActive && (
                    <span className="text-xs text-muted-foreground animate-pulse">{t('articleProgress.processing')}</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Success Result */}
        {result && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium">{result.title}</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div className="p-3 bg-secondary rounded-md">
                <div className="text-muted-foreground">{t('articleProgress.results.wordCount')}</div>
                <div className="text-lg font-medium">{result.word_count.toLocaleString()}</div>
              </div>
              <div className="p-3 bg-secondary rounded-md">
                <div className="text-muted-foreground">{t('articleProgress.results.seoScore')}</div>
                <div className="text-lg font-medium">{result.seo_score ?? '-'}/100</div>
              </div>
              <div className="p-3 bg-secondary rounded-md">
                <div className="text-muted-foreground">{t('articleProgress.results.geoScore')}</div>
                <div className="text-lg font-medium">{result.geo_score ?? '-'}/100</div>
              </div>
              <div className="p-3 bg-secondary rounded-md">
                <div className="text-muted-foreground">{t('articleProgress.results.humanScore')}</div>
                <div className="text-lg font-medium">{result.human_score ?? '-'}%</div>
              </div>
              <div className="p-3 bg-secondary rounded-md">
                <div className="text-muted-foreground">{t('articleProgress.results.generationCost')}</div>
                <div className="text-lg font-medium">${result.cost.toFixed(4)}</div>
              </div>
              {result.generation_time && (
                <div className="p-3 bg-secondary rounded-md">
                  <div className="text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {t('articleProgress.results.generationTime')}
                  </div>
                  <div className="text-lg font-medium">
                    {Math.floor(result.generation_time / 60)}:{String(Math.floor(result.generation_time % 60)).padStart(2, '0')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-4 rounded-md bg-destructive/10 text-destructive">
            <p className="font-medium mb-1">{t('articleProgress.generationFailed')}</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-4">
          {error && (
            <Button onClick={() => retryMutation.mutate()} disabled={retryMutation.isPending}>
              <RefreshCw
                className={`me-2 h-4 w-4 ${retryMutation.isPending ? 'animate-spin' : ''}`}
              />
              {t('app.retry')}
            </Button>
          )}
          {(result || error) && onClose && (
            <Button variant={result ? 'default' : 'outline'} onClick={onClose}>
              {result ? t('articleProgress.viewArticle') : t('app.close')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
